// src/tracker/pages/TransactionHistoryPage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton, Stack, Select, MenuItem, FormControl, InputLabel, Divider, ToggleButtonGroup, ToggleButton, Skeleton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import AddTransactionDialog from '../components/AddTransactionDialog';
import { useNotification } from '../../context/NotificationContext';
import { useConfirmation } from '../../context/ConfirmationContext';

/**
 * Displays a single summary metric.
 */
const SummaryItem = ({ title, amount, color, currencySymbol }) => (
    <Box
        textAlign="center"
        sx={{
            flex: 1,
            minWidth: '120px',
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
        }}
    >
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {title}
        </Typography>
        <Typography variant="h6" fontWeight="bold" color={color}>
            {currencySymbol}{amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
    </Box>
);

/**
 * Displays a full summary (Income, Spending, Net) for a single currency.
 */
const CurrencySummaryDisplay = ({ currency, summaryData }) => {
    const currencySymbol = currency === 'USD' ? '$' : '₱';
    return (
        <Box sx={{ width: '100%'}}>
             <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>{currency} Summary</Typography>
             <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 3 }}
                justifyContent="space-evenly"
                alignItems="center"
             >
                <SummaryItem title="Total Income" amount={summaryData.income} color="success.light" currencySymbol={currencySymbol} />
                <SummaryItem title="Total Spending" amount={summaryData.spending} color="error.light" currencySymbol={currencySymbol} />
                <SummaryItem title="Net" amount={summaryData.net} color={summaryData.net >= 0 ? 'info.light' : 'error.light'} currencySymbol={currencySymbol} />
            </Stack>
        </Box>
    );
}


const TransactionHistoryPage = () => {
    const confirm = useConfirmation();
    const { showNotification } = useNotification();

    const [transactions, setTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedDay, setSelectedDay] = useState('all');

    const [editingTransaction, setEditingTransaction] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    // **NEW**: State for exchange rate and overall summary currency
    const [exchangeRate, setExchangeRate] = useState(null);
    const [summaryCurrency, setSummaryCurrency] = useState('PHP');
    const phpSymbol = '₱';

    // **NEW**: Fetch and cache exchange rate
    useEffect(() => {
        const updateExchangeRate = async () => {
            try {
                const cachedData = JSON.parse(localStorage.getItem('exchangeRateData'));
                const today = new Date().setHours(0, 0, 0, 0);
                
                if (cachedData && new Date(cachedData.timestamp).setHours(0, 0, 0, 0) === today) {
                    setExchangeRate(cachedData.rate);
                    return;
                }

                const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
                if (!response.ok) throw new Error('Failed to fetch exchange rate');
                const data = await response.json();
                const rate = data.rates.PHP;
                
                setExchangeRate(rate);
                localStorage.setItem('exchangeRateData', JSON.stringify({ rate, timestamp: new Date().getTime() }));

            } catch (error) {
                console.error("Could not fetch exchange rate:", error);
                showNotification("Could not fetch exchange rate. Overall summary may be incomplete.", "warning");
            }
        };

        updateExchangeRate();
    }, [showNotification]);

    const fetchData = useCallback(() => {
        setLoading(true);
        const url = `https://frek.cc/proxy/38.54.86.24:8001/api/transactions?year=${selectedYear}&month=${selectedMonth}`;
        const transactionsPromise = fetch(url).then(res => res.json());
        const accountsPromise = fetch('https://frek.cc/proxy/38.54.86.24:8001/api/accounts').then(res => res.json());

        Promise.all([transactionsPromise, accountsPromise])
            .then(([transactionData, accountData]) => {
                setTransactions(Array.isArray(transactionData) ? transactionData : []);
                setAccounts(Array.isArray(accountData) ? accountData : []);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                showNotification('Error fetching data.', 'error');
                setTransactions([]);
                setAccounts([]);
            })
            .finally(() => setLoading(false));
    }, [selectedMonth, selectedYear, showNotification]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setSelectedDay('all');
    }, [selectedMonth, selectedYear]);

    const availableDays = useMemo(() => {
        if (!Array.isArray(transactions)) return [];
        const days = [...new Set(transactions.map(t => dayjs(t.date).date()))];
        return days.sort((a, b) => a - b);
    }, [transactions]);

    const filteredTransactions = useMemo(() => {
        if (!Array.isArray(transactions)) return [];
        if (selectedDay === 'all') return transactions;
        return transactions.filter(t => dayjs(t.date).date() === parseInt(selectedDay, 10));
    }, [transactions, selectedDay]);

    const summaryByCurrency = useMemo(() => {
        const summary = filteredTransactions.reduce((acc, curr) => {
            const amount = Number(curr.amount) || 0;
            const currency = curr.currency || 'unknown';

            if (!acc[currency]) {
                acc[currency] = { income: 0, spending: 0, net: 0 };
            }

            if (curr.type === 'income') acc[currency].income += amount;
            else acc[currency].spending += amount;
            
            return acc;
        }, {});

        for (const currency in summary) {
            summary[currency].net = summary[currency].income - summary[currency].spending;
        }

        return summary;
    }, [filteredTransactions]);

    // **NEW**: Calculate overall summary based on selected currency and exchange rate
    const overallSummary = useMemo(() => {
        const totals = { income: 0, spending: 0, net: 0 };
        if (!exchangeRate) return totals;

        for (const currency in summaryByCurrency) {
            const data = summaryByCurrency[currency];
            if (currency === summaryCurrency) {
                totals.income += data.income;
                totals.spending += data.spending;
            } else {
                if (summaryCurrency === 'USD') { // Convert PHP to USD
                    totals.income += data.income / exchangeRate;
                    totals.spending += data.spending / exchangeRate;
                } else { // Convert USD to PHP
                    totals.income += data.income * exchangeRate;
                    totals.spending += data.spending * exchangeRate;
                }
            }
        }
        totals.net = totals.income - totals.spending;
        return totals;
    }, [summaryByCurrency, exchangeRate, summaryCurrency]);

    
    const handleOpenEditDialog = (transaction) => { setEditingTransaction(transaction); setOpenDialog(true); };
    const handleCloseDialog = () => { setEditingTransaction(null); setOpenDialog(false); };
    const handleSummaryCurrencyChange = (event, newCurrency) => {
        if (newCurrency !== null) setSummaryCurrency(newCurrency);
    };

    const handleDelete = async (transactionId) => {
        const isConfirmed = await confirm({ title: 'Delete Transaction?', message: 'Are you sure you want to delete this transaction?' });
        if (isConfirmed) {
            setLoading(true);
            try {
                const response = await fetch(`https://frek.cc/proxy/38.54.86.24:8001/api/transactions/${transactionId}`, { method: 'DELETE' });
                if (!response.ok) throw new Error('Failed to delete transaction.');
                showNotification('Transaction deleted successfully!', 'success');
                fetchData();
            } catch (error) {
                console.error("Delete error:", error);
                showNotification(`Error: ${error.message}`, 'error');
                setLoading(false);
            }
        }
    };

    const handleSuccess = () => {
        handleCloseDialog();
        setLoading(true);
        fetchData();
    };
    
    const years = [2031, 2030, 2029,2028, 2027, 2026, 2025, 2024, 2023];
    const months = [
        {value: 1, label: 'January'}, {value: 2, label: 'February'}, {value: 3, label: 'March'},
        {value: 4, label: 'April'}, {value: 5, label: 'May'}, {value: 6, label: 'June'},
        {value: 7, label: 'July'}, {value: 8, label: 'August'}, {value: 9, 'label': 'September'},
        {value: 10, label: 'October'}, {value: 11, label: 'November'}, {value: 12, label: 'December'}
    ];

    return (
        <Box sx={{p: { xs: 1, sm: 2, md: 3}}}>
            {/* Header and Filters */}
            <Paper sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                    <Box>
                        <Typography variant="h4" component="h1">Transaction History</Typography>
                        <Typography variant="body2" color="text.secondary">Filter transactions by time period.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <FormControl size="small" sx={{minWidth: 120}}><InputLabel>Year</InputLabel><Select value={selectedYear} label="Year" onChange={(e) => setSelectedYear(e.target.value)}>{years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl>
                        <FormControl size="small" sx={{minWidth: 130}}><InputLabel>Month</InputLabel><Select value={selectedMonth} label="Month" onChange={(e) => setSelectedMonth(e.target.value)}>{months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}</Select></FormControl>
                        <FormControl size="small" sx={{minWidth: 120}} disabled={availableDays.length === 0}><InputLabel>Day</InputLabel><Select value={selectedDay} label="Day" onChange={(e) => setSelectedDay(e.target.value)}><MenuItem value="all">All Days</MenuItem>{availableDays.map(day => <MenuItem key={day} value={day}>{day}</MenuItem>)}</Select></FormControl>
                    </Stack>
                </Stack>
            </Paper>

            {/* Main Content Area */}
            <Box sx={{ position: 'relative' }}>
                 {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2, zIndex: 10 }}>
                        <CircularProgress />
                    </Box>
                )}
                <Stack spacing={3} sx={{ filter: loading ? 'blur(2px)' : 'none', transition: 'filter 0.2s' }}>

                    {/* **NEW** Overall Summary */}
                     <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
                         <Stack spacing={2} alignItems="center">
                             <Typography variant="h5" component="h2">Overall Summary</Typography>
                             <ToggleButtonGroup value={summaryCurrency} exclusive onChange={handleSummaryCurrencyChange} aria-label="summary currency">
                                 <ToggleButton value="USD">USD</ToggleButton>
                                 <ToggleButton value="PHP">PHP</ToggleButton>
                             </ToggleButtonGroup>
                             {exchangeRate ? (
                                <CurrencySummaryDisplay currency={summaryCurrency} summaryData={overallSummary} />
                             ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 2, width: '100%' }}>
                                    <Skeleton variant="rectangular" height={64} />
                                    <Skeleton variant="rectangular" height={64} />
                                </Box>
                             )}
                         </Stack>
                     </Paper>


                    {/* Individual Currency Summary */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
                        <Stack divider={<Divider orientation="vertical" flexItem />} spacing={2}>
                            {Object.keys(summaryByCurrency).length > 0 ? Object.entries(summaryByCurrency).map(([currency, data]) => (
                                <CurrencySummaryDisplay key={currency} currency={currency} summaryData={data} />
                            )) : <Typography sx={{textAlign: 'center', width: '100%', p: 2, color: 'text.secondary'}}>No data for this period.</Typography>}
                        </Stack>
                    </Paper>



                    {/* Transaction Table */}
                    <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="transaction table">
                            <TableHead sx={{ bgcolor: 'background.default' }}>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, idx) => (
                                        <TableRow key={`skeleton-${idx}`}>
                                            <TableCell><Skeleton variant="text" width={90} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={120} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={160} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={90} /></TableCell>
                                            <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
                                            <TableCell align="center"><Skeleton variant="circular" width={28} height={28} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : filteredTransactions.length > 0 ? filteredTransactions.map((row) => (
                                    <TableRow key={row._id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>{dayjs(row.date).format('YYYY-MM-DD')}</TableCell>
                                        <TableCell>{row.account_name}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell>{row.category}</TableCell>
                                        <TableCell align="right">
                                            <Typography color={row.type === 'income' ? 'success.main' : 'error.main'} fontWeight="500">
                                                {row.type === 'income' ? '+' : '-'}{row.currency === 'USD' ? '$' : phpSymbol}{Number(row.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleOpenEditDialog(row)}><EditIcon fontSize="small"/></IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(row._id)}><DeleteIcon fontSize="small"/></IconButton>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={6} align="center" sx={{py: 4}}>No transactions found.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </Box>
            
            <AddTransactionDialog open={openDialog} onClose={handleCloseDialog} onSuccess={handleSuccess} accounts={accounts} transactionToEdit={editingTransaction}/>
        </Box>
    );
};

export default TransactionHistoryPage;

